import moment from "moment";
import config from "../../../../../config";
import { hasPositiveNumericValue, hasRawNumericValue } from "../../shared/haklaiFlowHelpers";
import { INHERITANCE_TESTATOR_BASIS_START_DATE } from "../../shared/haklaiConstants";
import { getPropertyPartsValidationError } from "../../propertyParts/logic/haklaiPropertyPartsGates";
import {
  getHaklaiSoldDateBeforeEarliestError,
  HAKLAI_PRE_CALC_ERRORS,
  HAKLAI_PRE_CALC_MESSAGES
} from "../haklaiPreCalcTexts";

const parseHaklaiDateValue = (value) => {
  if (!value) return null;
  if (moment.isMoment(value)) return value.isValid() ? value : null;
  if (value instanceof Date) {
    const parsedDate = moment(value);
    return parsedDate.isValid() ? parsedDate : null;
  }
  const parsedDate = moment(value, ["YYYY-MM-DD", "DD/MM/YYYY"], true);
  return parsedDate.isValid() ? parsedDate : null;
};

const hasValidDateValue = (value) => Boolean(parseHaklaiDateValue(value));
const earliestAllowedDate = moment(config.earliestAllowedProperySoldDate);

export const HAKLAI_INVALID_PARCEL_MESSAGE = HAKLAI_PRE_CALC_ERRORS.invalidParcel;

/** GovMap returned no parcel rows for the address search. */
export const HAKLAI_ADDRESS_NOT_FOUND_MESSAGE = HAKLAI_PRE_CALC_ERRORS.addressNotFound;

/** Network or unexpected failure during address → parcel search. */
export const HAKLAI_ADDRESS_SEARCH_FAILED_MESSAGE = HAKLAI_PRE_CALC_ERRORS.addressSearchFailed;

// ── Field-level inline errors for the pre-calc form ────────────────────────
// Returns an object of { fieldName: errorMessage } for all invalid fields.
// Used for both inline display and section-gating logic.
export const getHaklaiPreCalcInlineErrors = (preCalc = {}) => {
  const acquisitionDetails = preCalc.acquisitionDetails || {};
  const wayOfGettingProperty = preCalc.wayOfGettingProperty;
  const errors = {};
  const today = moment();
  const soldDate = parseHaklaiDateValue(preCalc.soldPropertyDate);

  if (!hasValidDateValue(preCalc.soldPropertyDate)) {
    errors.soldPropertyDate = HAKLAI_PRE_CALC_ERRORS.soldDateInvalid;
  } else if (soldDate.isAfter(today, "day")) {
    errors.soldPropertyDate = HAKLAI_PRE_CALC_ERRORS.soldDateFuture;
  } else if (soldDate.isBefore(earliestAllowedDate, "day")) {
    errors.soldPropertyDate = getHaklaiSoldDateBeforeEarliestError(config.earliestAllowedProperySoldDate);
  }

  if (!hasPositiveNumericValue(preCalc.totalPropertyCost)) {
    errors.totalPropertyCost = HAKLAI_PRE_CALC_ERRORS.saleValueRequired;
  }

  if (!wayOfGettingProperty) {
    errors.wayOfGettingProperty = HAKLAI_PRE_CALC_ERRORS.wayOfGettingPropertyRequired;
  }

  if (wayOfGettingProperty === "purchase" || wayOfGettingProperty === "gift") {
    if (!hasValidDateValue(acquisitionDetails.deduDate)) {
      errors.deduDate = HAKLAI_PRE_CALC_ERRORS.acquisitionDateInvalid;
    } else {
      const deduDate = parseHaklaiDateValue(acquisitionDetails.deduDate);
      if (deduDate.isAfter(today, "day")) {
        errors.deduDate = HAKLAI_PRE_CALC_ERRORS.acquisitionDateFuture;
      } else if (soldDate && deduDate.isAfter(soldDate, "day")) {
        errors.deduDate = HAKLAI_PRE_CALC_ERRORS.acquisitionDateAfterSale;
      }
    }
  }

  if (wayOfGettingProperty === "inheritance") {
    if (!hasValidDateValue(acquisitionDetails.testatorPassingDate)) {
      errors.testatorPassingDate = HAKLAI_PRE_CALC_ERRORS.testatorPassingDateInvalid;
    } else {
      const passingDate = parseHaklaiDateValue(acquisitionDetails.testatorPassingDate);
      if (passingDate.isAfter(today, "day")) {
        errors.testatorPassingDate = HAKLAI_PRE_CALC_ERRORS.testatorPassingDateFuture;
      } else if (soldDate && passingDate.isAfter(soldDate, "day")) {
        errors.testatorPassingDate = HAKLAI_PRE_CALC_ERRORS.testatorPassingDateAfterSale;
      }

      if (passingDate.isSameOrAfter(moment(INHERITANCE_TESTATOR_BASIS_START_DATE), "day")) {
        if (!hasValidDateValue(acquisitionDetails.testatorBoughtPropertyDate)) {
          errors.testatorBoughtPropertyDate = HAKLAI_PRE_CALC_ERRORS.testatorPurchaseDateRequired;
        } else {
          const boughtDate = parseHaklaiDateValue(acquisitionDetails.testatorBoughtPropertyDate);
          if (boughtDate.isAfter(today, "day")) {
            errors.testatorBoughtPropertyDate = HAKLAI_PRE_CALC_ERRORS.testatorPurchaseDateFuture;
          } else if (soldDate && boughtDate.isAfter(soldDate, "day")) {
            errors.testatorBoughtPropertyDate = HAKLAI_PRE_CALC_ERRORS.testatorPurchaseDateAfterSale;
          } else if (boughtDate.isAfter(passingDate, "day")) {
            errors.testatorBoughtPropertyDate = HAKLAI_PRE_CALC_ERRORS.testatorPurchaseDateAfterPassing;
          }
        }
      }
    }
  }

  if (!hasPositiveNumericValue(acquisitionDetails.amount)) {
    errors.amount = HAKLAI_PRE_CALC_ERRORS.purchaseAmountRequired;
  }

  return errors;
};

// ── Field order for sequential prerequisite checking ──────────────────────
export const getPropertyPartsPrerequisiteFieldNames = (preCalc = {}) => {
  const acquisitionDetails = preCalc.acquisitionDetails || {};
  const wayOfGettingProperty = preCalc.wayOfGettingProperty;
  const fieldNames = ["soldPropertyDate", "totalPropertyCost", "wayOfGettingProperty"];

  if (wayOfGettingProperty === "purchase" || wayOfGettingProperty === "gift") {
    fieldNames.push("deduDate", "amount");
  }

  if (wayOfGettingProperty === "inheritance") {
    fieldNames.push("testatorPassingDate");
    if (
      hasValidDateValue(acquisitionDetails.testatorPassingDate)
      && parseHaklaiDateValue(acquisitionDetails.testatorPassingDate).isSameOrAfter(moment(INHERITANCE_TESTATOR_BASIS_START_DATE), "day")
    ) {
      fieldNames.push("testatorBoughtPropertyDate");
    }
    fieldNames.push("amount");
  }

  return fieldNames;
};

const PRE_CALC_SUBMIT_FIELD_ORDER = [
  "soldPropertyDate",
  "totalPropertyCost",
  "wayOfGettingProperty",
  "deduDate",
  "testatorPassingDate",
  "testatorBoughtPropertyDate",
  "amount"
];

export const getHaklaiPreCalcSubmitErrorMessage = (preCalc = {}) => {
  const errors = getHaklaiPreCalcInlineErrors(preCalc);
  const firstErrorField = PRE_CALC_SUBMIT_FIELD_ORDER.find((fieldName) => Boolean(errors[fieldName]));
  return firstErrorField ? errors[firstErrorField] : null;
};

// ── Lock/unlock state for the property parts section ──────────────────────
export const getPropertyPartsPrerequisiteState = (preCalc = {}) => {
  const buildLockedState = (blockingField, blockingFieldMessage) => ({
    unlocked: false,
    reason: HAKLAI_PRE_CALC_MESSAGES.propertyPartsPrerequisite,
    blockingSection: "preCalc",
    blockingField,
    blockingFieldMessage
  });

  const errors = getHaklaiPreCalcInlineErrors(preCalc);
  const fieldOrder = getPropertyPartsPrerequisiteFieldNames(preCalc);
  const firstBlockingField = fieldOrder.find((fieldName) => Boolean(errors[fieldName]));

  if (firstBlockingField) {
    return buildLockedState(
      firstBlockingField,
      errors[firstBlockingField]
    );
  }

  return { unlocked: true, reason: "", blockingSection: "", blockingField: "", blockingFieldMessage: "" };
};

// ── Visible inline errors (respects touch state for progressive disclosure) ─
export const getPropertyPartsPrerequisiteFieldErrors = (preCalc = {}, touchedFields = {}) => {
  const allErrors = getHaklaiPreCalcInlineErrors(preCalc);
  const acquisitionDetails = preCalc.acquisitionDetails || {};

  const shouldRevealFieldError = (fieldName) => {
    if (touchedFields[fieldName]) return true;
    if (fieldName === "soldPropertyDate") return Boolean(preCalc.soldPropertyDate);
    if (fieldName === "totalPropertyCost") return hasRawNumericValue(preCalc.totalPropertyCost);
    if (fieldName === "deduDate") return Boolean(acquisitionDetails.deduDate);
    if (fieldName === "testatorPassingDate") return Boolean(acquisitionDetails.testatorPassingDate);
    if (fieldName === "testatorBoughtPropertyDate") return Boolean(acquisitionDetails.testatorBoughtPropertyDate);
    if (fieldName === "amount") return hasRawNumericValue(acquisitionDetails.amount);
    return false;
  };

  return Object.keys(allErrors).reduce((acc, fieldName) => {
    if (shouldRevealFieldError(fieldName)) {
      acc[fieldName] = allErrors[fieldName];
    }
    return acc;
  }, {});
};

// ── Lock/unlock state for the expenses section ────────────────────────────
export const getExpensesSectionState = ({
  preCalc = {},
  propertyPartsData = {},
  totalPropertyCost
} = {}) => {
  const propertyPartsPrerequisiteState = getPropertyPartsPrerequisiteState(preCalc);
  if (!propertyPartsPrerequisiteState.unlocked) {
    return {
      unlocked: false,
      reason: HAKLAI_PRE_CALC_MESSAGES.expensesPreCalcPrerequisite,
      blockingSection: "preCalc"
    };
  }

  const propertyPartsError = getPropertyPartsValidationError({
    preCalc,
    propertyPartsData,
    totalPropertyCost
  });
  if (propertyPartsError) {
    return {
      unlocked: false,
      reason: HAKLAI_PRE_CALC_MESSAGES.expensesPropertyPartsPrerequisite,
      blockingSection: "propertyParts"
    };
  }

  return { unlocked: true, reason: "", blockingSection: "" };
};
